import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import type { AuthUser, LoginResponse } from '@/types/auth';

/**
 * Log in and return the response in this app's `LoginResponse` shape.
 *
 * The monolith answers `{ user, tokens: { accessToken, refreshToken }, session }`.
 * identity-service answers `{ user, accessToken, expiresIn }` — no `tokens`
 * wrapper, no `session`, and no `status` on the user (its refresh token is an
 * httpOnly cookie and is deliberately absent from the body). Normalising here
 * keeps that difference out of every consumer: the store, the axios refresh
 * interceptor and LoginPage all keep reading `res.tokens.accessToken`.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  try {
    const res = await http.post<ApiEnvelope<LoginResponse>>('/auth/login', { email, password });
    const data = unwrap(res.data) as LoginResponse & {
      accessToken?: string;
      refreshToken?: string;
      expiresIn?: number;
    };

    const tokens = data.tokens ?? {
      accessToken: data.accessToken ?? '',
      // Empty, not undefined: identity-service keeps the refresh token in an
      // httpOnly cookie, so the browser replays it on /auth/refresh without
      // the app ever holding it.
      refreshToken: data.refreshToken ?? '',
      expiresIn: data.expiresIn,
    };

    return {
      ...data,
      tokens,
      // identity-service omits `status` for an account it agreed to log in;
      // absent therefore means active, and defaulting keeps LoginPage's
      // "account is not active" branch from rejecting every services-stack login.
      user: { ...data.user, status: data.user?.status ?? 'ACTIVE' },
    };
  } catch (error) {
    throw toApiError(error);
  }
}

export async function fetchMe(): Promise<AuthUser> {
  try {
    // `/me/profile`, not `/me`. Neither the monolith nor identity-service has ever
    // served a bare `/me` — this call 404'd against both, so the POS never had the
    // signed-in user and fell back to whatever the login response left in the store.
    const res = await http.get<ApiEnvelope<AuthUser>>('/me/profile');
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
